import { useState } from 'react'
import { Link } from 'react-router'
import { formatDistanceToNow } from 'date-fns'
import { useAdminLayouts } from '@/hooks/useAdminLayouts'
import { PageHeader } from '@/components/shared/PageHeader'
import { ThumbnailPreview } from '@/components/shared/ThumbnailPreview'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Search, Globe, Lock, Layout, ChevronLeft, ChevronRight } from 'lucide-react'

export function LayoutBrowserPage() {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<'all' | 'public' | 'private'>('all')
  const [includeDeleted, setIncludeDeleted] = useState(false)
  const [page, setPage] = useState(1)
  const pageSize = 24

  const { data, isLoading } = useAdminLayouts({
    page,
    pageSize,
    search: search || undefined,
    visibility: filter,
    includeDeleted,
  })

  const layouts = data?.layouts ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <div>
      <PageHeader title="Layouts" description={`${total} layouts across all users`} />

      <div className="mb-6 flex flex-wrap items-center gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name or user ID..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            className="pl-9"
          />
        </div>
        <div className="flex gap-1">
          {(['all', 'public', 'private'] as const).map((f) => (
            <button
              key={f}
              onClick={() => {
                setFilter(f)
                setPage(1)
              }}
              className={`rounded-md px-3 py-1.5 text-sm ${
                filter === f
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {f === 'all' ? 'All' : f === 'public' ? 'Public' : 'Private'}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={includeDeleted}
            onChange={(e) => {
              setIncludeDeleted(e.target.checked)
              setPage(1)
            }}
          />
          Include deleted
        </label>
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-48 rounded-lg" />
          ))}
        </div>
      ) : layouts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <Layout className="mb-4 h-12 w-12" />
          <p>No layouts found.</p>
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {layouts.map((layout) => (
              <Link key={layout.id} to={`/layouts/${layout.id}`} className="group">
                <Card className="overflow-hidden transition-shadow group-hover:shadow-md">
                  <div className="relative h-32 bg-muted">
                    <ThumbnailPreview
                      src={layout.thumbnail_url}
                      alt={layout.name}
                      kind="layout"
                      className="h-full w-full rounded-none"
                      iconClassName="h-8 w-8"
                    />
                    <div className="absolute right-2 top-2 flex gap-1">
                      {layout.deleted_at && (
                        <Badge variant="destructive">Deleted</Badge>
                      )}
                      {layout.is_public ? (
                        <Badge className="bg-green-100 text-green-800">
                          <Globe className="mr-1 h-3 w-3" /> Public
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          <Lock className="mr-1 h-3 w-3" /> Private
                        </Badge>
                      )}
                    </div>
                  </div>
                  <CardContent className="p-3">
                    <p className="truncate text-sm font-medium">{layout.name}</p>
                    <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                      <span className="truncate">
                        {layout.locations?.name ?? 'Unknown'}
                        {layout.locations?.airport_code && ` (${layout.locations.airport_code})`}
                      </span>
                      <span className="shrink-0">
                        {formatDistanceToNow(new Date(layout.updated_at), { addSuffix: true })}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-2 text-[11px]">
                      <span className="truncate text-muted-foreground/80">
                        {layout.owner.display_name || layout.owner.email || layout.user_id.slice(0, 8)}
                      </span>
                      <Badge variant="outline" className="shrink-0 text-[10px] capitalize">
                        {layout.owner.tier.replace('_', ' ')}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft className="h-4 w-4" /> Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
